import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaHeart, FaClock, FaFire, FaArrowRight } from 'react-icons/fa';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FavoriteRecipesWidget
 * Muestra hasta 3 recetas favoritas del usuario en el dashboard,
 * con acceso directo a la Cocina Inteligente.  Solo se renderiza
 * cuando el usuario tenga favoritas.
 */
export default function FavoriteRecipesWidget({ user }) {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = useCallback(async () => {
    if (!user?.email) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/api/personalized-plans/fit-evolution/favorite-recipes/${encodeURIComponent(user.email)}`);
      setFavorites((res.data?.favorites || []).slice(0, 3));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  if (loading) return null;
  if (favorites.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 mb-6"
      style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.10), rgba(0,240,255,0.04))', border: '1px solid rgba(var(--brand-primary-glow),0.25)' }}
      data-testid="favorite-recipes-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FaHeart className="text-pink-400" />
          <h3 className="text-white font-bold text-base uppercase tracking-wide" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Mis recetas favoritas
          </h3>
        </div>
        <button
          onClick={() => navigate('/fit-evolution/recipes')}
          className="text-xs text-white/70 hover:text-white flex items-center gap-1 font-semibold"
          data-testid="favorites-widget-see-all"
        >
          Ver mi plan <FaArrowRight />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {favorites.map((f, i) => {
          const r = f.recipe || {};
          return (
            <button
              key={f.recipe_id}
              onClick={() => navigate('/fit-evolution/recipes')}
              data-testid={`favorite-widget-item-${i}`}
              className="text-left rounded-xl p-3 transition-all hover:scale-[1.02]"
              style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <p className="text-[10px] uppercase tracking-wider text-pink-300/80 mb-1">{f.meal_time}</p>
              <p className="text-white font-bold text-sm line-clamp-2 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{r.name || 'Receta'}</p>
              <div className="flex items-center gap-3 text-[10px] text-white/60">
                {r.prep_minutes && <span className="flex items-center gap-1"><FaClock /> {r.prep_minutes} min</span>}
                {r.calories && <span className="flex items-center gap-1"><FaFire /> {r.calories} kcal</span>}
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
